/**
 * Ferramentas — os exemplares com patrimônio, e o ciclo deles (05/08, Lucas).
 *
 * Três visões: NO CD (cadastrar exemplar, histórico), EMPRESTADAS (prazo,
 * devolução com foto + nova classificação, estender prazo) e CONCERTO (a fila
 * das estragadas → gerar romaneio para a loja com folhas impressas → registrar
 * a volta com valor e conferência da empresa).
 */

import { api, ErroApi } from '../api.js';
import { dataHora, escapar, numero, quando } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';
import { montarBuscaItem, type ItemEscolhido } from '../comum/busca-item.js';

const ESTADO_ROTULO: Record<string, string> = {
  novo: 'Novo',
  usado_excelente: 'Usado excelente',
  usado_bom: 'Usado bom',
  usado_gasto: 'Usado gasto',
  estragado: 'Estragado',
};

interface Unidade {
  id: number;
  patrimonio: string;
  numero_serie: string | null;
  estado: string;
  situacao: string;
  defeito: string | null;
  obs: string | null;
  item: string;
  itemNome: string;
  miniatura: string | null;
  foto: string | null;
  emprestimoId: number | null;
  reservado_ate: string | null;
  responsavel_nome: string | null;
  saiu_em: string | null;
  prazo_estendido_em: string | null;
  obraNome: string | null;
  vencido: boolean;
  concertoId: number | null;
  concertoDefeito: string | null;
  empresa_motorista: string | null;
  enviado_em: string | null;
  deixado_em: string | null;
  concertoRomaneio: string | null;
}

function dataBr(iso: string | null): string {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
}

export function montarFerramentas(raiz: HTMLElement): void {
  let visao: 'no_cd' | 'emprestada' | 'concerto' | 'cadastrar' = 'no_cd';

  raiz.innerHTML = `
    <div class="chips" id="fe-visoes"></div>
    <div id="fe-corpo"><p class="carregando">Carregando…</p></div>`;
  const corpo = raiz.querySelector<HTMLElement>('#fe-corpo')!;

  function desenharVisoes(): void {
    const opcoes: [typeof visao, string][] = [
      ['no_cd', 'No CD'],
      ['emprestada', 'Emprestadas'],
      ['concerto', 'Concerto'],
      ['cadastrar', '+ Cadastrar exemplar'],
    ];
    raiz.querySelector('#fe-visoes')!.innerHTML = opcoes
      .map(([v, t]) => `<button class="chip" data-v="${v}" aria-pressed="${visao === v}">${t}</button>`)
      .join('');
  }
  raiz.querySelector('#fe-visoes')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (!b) return;
    visao = b.dataset['v'] as typeof visao;
    desenharVisoes();
    void abrirVisao();
  });

  async function buscarUnidades(filtro: string): Promise<Unidade[]> {
    const r = await api.get<{ unidades: Unidade[] }>(`/api/ferramentas${filtro}`);
    return r.unidades;
  }

  async function abrirVisao(): Promise<void> {
    corpo.innerHTML = `<p class="carregando">Carregando…</p>`;
    try {
      if (visao === 'cadastrar') return desenharCadastro();
      if (visao === 'no_cd') return desenharNoCd(await buscarUnidades('?situacao=no_cd'));
      if (visao === 'emprestada') return desenharEmprestadas(await buscarUnidades('?situacao=emprestada'));
      const [fila, fora] = await Promise.all([
        buscarUnidades('?situacao=aguardando_concerto'),
        buscarUnidades('?situacao=em_concerto'),
      ]);
      desenharConcerto(fila, fora);
    } catch (erro) {
      corpo.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar as ferramentas.',
      )}</p>`;
    }
  }

  function cartaoBase(u: Unidade, extra: string, acoes: string): string {
    return `
      <div class="fe-card" data-pat="${escapar(u.patrimonio)}">
        <span class="ped-cfoto">${
          u.miniatura
            ? `<img src="${u.miniatura}" alt="" ${u.foto ? `data-grande="${escapar(api.foto(u.foto) ?? '')}"` : ''}>`
            : '<span class="sem">—</span>'
        }</span>
        <span class="ped-cnome">
          <b>${escapar(u.patrimonio)} — ${escapar(u.itemNome)}</b>
          <span class="sub-inline">${u.numero_serie ? `série ${escapar(u.numero_serie)} · ` : ''}${
            ESTADO_ROTULO[u.estado] ?? u.estado
          }</span>
          ${extra}
        </span>
        <span class="fe-acoes">${acoes}</span>
        <div class="fe-detalhe" hidden></div>
      </div>`;
  }

  // ------------------------------------------------------------------ no CD

  function desenharNoCd(unidades: Unidade[]): void {
    corpo.innerHTML = `
      <section class="ped-painel">
        <h3>🔧 No CD <span class="sub-inline">${unidades.length} exemplar(es) disponível(is)</span></h3>
        ${
          unidades.length === 0
            ? `<p class="dica">Nenhum exemplar no CD. Cadastre em "+ Cadastrar exemplar".</p>`
            : unidades
                .map((u) => cartaoBase(u, '', `<button class="btn btn-claro" data-hist>Histórico</button>`))
                .join('')
        }
      </section>`;
    ligarComuns();
  }

  // ------------------------------------------------------------- emprestadas

  function desenharEmprestadas(unidades: Unidade[]): void {
    const vencidas = unidades.filter((u) => u.vencido);
    corpo.innerHTML = `
      <section class="ped-painel">
        <h3>🔧 Emprestadas <span class="sub-inline">${unidades.length} fora${
          vencidas.length ? ` · ${vencidas.length} com prazo vencido` : ''
        }</span></h3>
        ${
          unidades.length === 0
            ? `<p class="dica">Nenhuma ferramenta fora do CD.</p>`
            : unidades
                .map((u) =>
                  cartaoBase(
                    u,
                    `<span class="${u.vencido ? 'fe-vencido' : 'sub-inline'}">
                       ${u.vencido ? '⚠️ RECOLHER — prazo venceu em' : 'reservada até'} ${dataBr(u.reservado_ate)}
                       · com ${escapar(u.responsavel_nome ?? '?')}${u.obraNome ? ` · ${escapar(u.obraNome)}` : ''}
                     </span>`,
                    `<button class="btn" data-devolver>Devolução…</button>
                     <button class="btn btn-claro" data-prazo>Estender prazo…</button>
                     <button class="btn btn-claro" data-hist>Histórico</button>`,
                  ),
                )
                .join('')
        }
      </section>`;
    ligarComuns();
  }

  // ---------------------------------------------------------------- concerto

  function desenharConcerto(fila: Unidade[], fora: Unidade[]): void {
    corpo.innerHTML = `
      <section class="ped-painel">
        <h3>🛠️ Para enviar ao concerto <span class="sub-inline">${fila.length} na fila</span></h3>
        ${
          fila.length === 0
            ? `<p class="dica">Nada na fila — estragou na devolução, cai aqui.</p>`
            : `${fila
                .map(
                  (u) => `
                <label class="fe-card fe-selecionavel">
                  <input type="checkbox" data-sel value="${escapar(u.patrimonio)}">
                  <span class="ped-cfoto">${u.miniatura ? `<img src="${u.miniatura}" alt="">` : '<span class="sem">—</span>'}</span>
                  <span class="ped-cnome">
                    <b>${escapar(u.patrimonio)} — ${escapar(u.itemNome)}</b>
                    <span class="fe-vencido">defeito: ${escapar(u.defeito ?? '?')}</span>
                  </span>
                </label>`,
                )
                .join('')}
              <div class="conf-acoes">
                <label class="rotulado pf-motorista"><span>Motorista (opcional)</span>
                  <input id="fe-conc-motorista" maxlength="120"></label>
                <button class="btn" id="fe-gerar-concerto">📋 Gerar romaneio de concerto</button>
              </div>`
        }
      </section>

      <section class="ped-painel">
        <h3>🏪 No concerto <span class="sub-inline">${fora.length} fora</span></h3>
        ${
          fora.length === 0
            ? `<p class="dica">Nenhuma ferramenta na loja agora.</p>`
            : fora
                .map((u) =>
                  cartaoBase(
                    u,
                    `<span class="sub-inline">romaneio ${escapar(u.concertoRomaneio ?? '?')} ·
                      saiu ${u.enviado_em ? quando(u.enviado_em) : '—'} ·
                      ${
                        u.empresa_motorista
                          ? `na ${escapar(u.empresa_motorista)}`
                          : 'o motorista ainda não registrou a empresa'
                      }</span>`,
                    `<button class="btn" data-voltou>Voltou do concerto…</button>
                     ${u.concertoRomaneio ? `<button class="btn btn-claro" data-folhas="${escapar(u.concertoRomaneio)}">🖨 Folhas</button>` : ''}
                     <button class="btn btn-claro" data-hist>Histórico</button>`,
                  ),
                )
                .join('')
        }
      </section>`;

    corpo.querySelector('#fe-gerar-concerto')?.addEventListener('click', () => {
      const marcados = [...corpo.querySelectorAll<HTMLInputElement>('[data-sel]:checked')].map(
        (c) => c.value,
      );
      if (marcados.length === 0) {
        avisarBloqueante('Marque as ferramentas que vão para o concerto.');
        return;
      }
      const motorista = corpo.querySelector<HTMLInputElement>('#fe-conc-motorista')!.value.trim();
      void api
        .post<{ romaneio: string; ferramentas: number }>('/api/ferramentas/concerto/romaneio', {
          patrimonios: marcados,
          ...(motorista ? { motorista } : {}),
        })
        .then((r) => {
          avisar(`Romaneio ${r.romaneio} criado com ${r.ferramentas} ferramenta(s).`);
          void imprimirFolhas(r.romaneio);
          void abrirVisao();
        })
        .catch((e) =>
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui gerar o romaneio.'),
        );
    });
    corpo.querySelectorAll<HTMLElement>('[data-folhas]').forEach((b) => {
      b.addEventListener('click', () => void imprimirFolhas(b.dataset['folhas']!));
    });
    ligarComuns();
  }

  // ---------------------------------------------------------------- cadastro

  function desenharCadastro(): void {
    let item: ItemEscolhido | undefined;
    let fotoCaminho: string | undefined;
    corpo.innerHTML = `
      <section class="ped-painel">
        <h3>➕ Cadastrar exemplar</h3>
        <p class="dica">O item genérico precisa estar marcado como "controla unidades
          (patrimônio)" no cadastro de Itens. Carrinho de mão e enxada não precisam
          disso — só o que vale rastrear individualmente.</p>
        <div id="fe-cad-item"></div>
        <div id="fe-cad-escolhido" class="dica"></div>
        <div class="form-2col">
          <label class="rotulado"><span>Nº de patrimônio</span><input id="fe-pat" maxlength="60"></label>
          <label class="rotulado"><span>Nº de série</span><input id="fe-serie" maxlength="120"></label>
          <label class="rotulado"><span>Estado</span>
            <select id="fe-estado">${['novo', 'usado_excelente', 'usado_bom', 'usado_gasto']
              .map((e) => `<option value="${e}">${ESTADO_ROTULO[e]}</option>`)
              .join('')}</select></label>
          <label class="rotulado"><span>Foto</span><input type="file" id="fe-foto" accept="image/*"></label>
        </div>
        <div class="conf-acoes"><button class="btn btn-grande" id="fe-criar">✓ Cadastrar</button></div>
      </section>`;

    montarBuscaItem(corpo.querySelector<HTMLElement>('#fe-cad-item')!, (i) => {
      item = i;
      corpo.querySelector('#fe-cad-escolhido')!.textContent = `Item: ${i.codigo} — ${i.nome}`;
    });
    corpo.querySelector('#fe-criar')!.addEventListener('click', () => {
      void (async () => {
        if (!item) return avisarBloqueante('Escolha o item genérico da ferramenta.');
        const pat = corpo.querySelector<HTMLInputElement>('#fe-pat')!.value.trim();
        if (!pat) return avisarBloqueante('Informe o número de patrimônio.');
        const arq = corpo.querySelector<HTMLInputElement>('#fe-foto')!.files?.[0];
        try {
          if (arq && !fotoCaminho) {
            fotoCaminho = (await api.enviarFoto('itens', arq, pat)).caminho;
          }
          await api.post('/api/ferramentas', {
            item: item.codigo,
            patrimonio: pat,
            numeroSerie: corpo.querySelector<HTMLInputElement>('#fe-serie')!.value.trim() || undefined,
            estado: corpo.querySelector<HTMLSelectElement>('#fe-estado')!.value,
            ...(fotoCaminho ? { fotoUrl: fotoCaminho } : {}),
          });
          avisar(`Exemplar ${pat} cadastrado.`);
          visao = 'no_cd';
          desenharVisoes();
          void abrirVisao();
        } catch (e) {
          const msg = e instanceof ErroApi ? e.message + (e.sugestao ? ` ${e.sugestao}` : '') : 'Não consegui cadastrar.';
          avisarBloqueante(msg);
        }
      })();
    });
  }

  // ---------------------------------------------- ações comuns (delegação)

  function ligarComuns(): void {
    corpo.querySelectorAll<HTMLElement>('[data-hist]').forEach((b) => {
      b.addEventListener('click', () => {
        void historico(b.closest<HTMLElement>('[data-pat]')!.dataset['pat']!);
      });
    });
    corpo.querySelectorAll<HTMLElement>('[data-devolver]').forEach((b) => {
      b.addEventListener('click', () => abrirDevolucao(b.closest<HTMLElement>('[data-pat]')!));
    });
    corpo.querySelectorAll<HTMLElement>('[data-prazo]').forEach((b) => {
      b.addEventListener('click', () => {
        const pat = b.closest<HTMLElement>('[data-pat]')!.dataset['pat']!;
        const nova = prompt('Novo prazo (AAAA-MM-DD) — depois de combinar com o responsável:');
        if (!nova) return;
        void api
          .post(`/api/ferramentas/${encodeURIComponent(pat)}/prazo`, { reservadoAte: nova.trim() })
          .then(() => {
            avisar('Prazo estendido.');
            void abrirVisao();
          })
          .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui estender.'));
      });
    });
    corpo.querySelectorAll<HTMLElement>('[data-voltou]').forEach((b) => {
      b.addEventListener('click', () => abrirVolta(b.closest<HTMLElement>('[data-pat]')!));
    });
  }

  function abrirDevolucao(card: HTMLElement): void {
    const pat = card.dataset['pat']!;
    const det = card.querySelector<HTMLElement>('.fe-detalhe')!;
    det.hidden = false;
    det.innerHTML = `
      <div class="form-2col">
        <label class="rotulado"><span>Como voltou?</span>
          <select data-estado>${['usado_excelente', 'usado_bom', 'usado_gasto', 'estragado']
            .map((e) => `<option value="${e}">${ESTADO_ROTULO[e]}</option>`)
            .join('')}</select></label>
        <label class="rotulado"><span>Foto da devolução (obrigatória)</span>
          <input type="file" data-foto accept="image/*"></label>
      </div>
      <label class="rotulado" data-defeito-caixa hidden><span>Defeito aparente</span>
        <input data-defeito maxlength="300" placeholder="não liga · liga porém fraca · disco travado…"></label>
      <div class="conf-acoes">
        <button class="btn" data-confirmar>✓ Registrar devolução</button>
        <button class="btn btn-claro" data-fechar>Cancelar</button>
      </div>`;

    det.querySelector('[data-estado]')!.addEventListener('change', (e) => {
      det.querySelector<HTMLElement>('[data-defeito-caixa]')!.hidden =
        (e.target as HTMLSelectElement).value !== 'estragado';
    });
    det.querySelector('[data-fechar]')!.addEventListener('click', () => {
      det.hidden = true;
    });
    det.querySelector('[data-confirmar]')!.addEventListener('click', () => {
      void (async () => {
        const estado = det.querySelector<HTMLSelectElement>('[data-estado]')!.value;
        const arq = det.querySelector<HTMLInputElement>('[data-foto]')!.files?.[0];
        const defeito = det.querySelector<HTMLInputElement>('[data-defeito]')!.value.trim();
        if (!arq) return avisarBloqueante('A devolução exige a foto da ferramenta.');
        if (estado === 'estragado' && !defeito) {
          return avisarBloqueante('Estragada precisa do defeito aparente.');
        }
        try {
          const foto = await api.enviarFoto('itens', arq, pat);
          const r = await api.post<{ paraConcerto: boolean }>(
            `/api/ferramentas/${encodeURIComponent(pat)}/devolucao`,
            { estado, fotoUrl: foto.caminho, ...(defeito ? { defeito } : {}) },
          );
          avisar(
            r.paraConcerto
              ? `${pat} devolvida como estragada — está na fila de concerto.`
              : `${pat} devolvida ao CD.`,
          );
          void abrirVisao();
        } catch (e) {
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui registrar.');
        }
      })();
    });
  }

  function abrirVolta(card: HTMLElement): void {
    const pat = card.dataset['pat']!;
    const det = card.querySelector<HTMLElement>('.fe-detalhe')!;
    det.hidden = false;
    det.innerHTML = `
      <div class="form-2col">
        <label class="rotulado"><span>Foto na volta (obrigatória)</span>
          <input type="file" data-foto accept="image/*"></label>
        <label class="rotulado"><span>Valor do concerto (R$)</span>
          <input type="number" min="0" step="0.01" data-valor inputmode="decimal"></label>
        <label class="rotulado"><span>Empresa no orçamento</span>
          <input data-empresa maxlength="200" placeholder="como está escrito no papel"></label>
        <label class="rotulado"><span>Estado após o concerto</span>
          <select data-estado>${['usado_excelente', 'usado_bom', 'usado_gasto']
            .map((e) => `<option value="${e}">${ESTADO_ROTULO[e]}</option>`)
            .join('')}</select></label>
      </div>
      <div class="conf-acoes">
        <button class="btn" data-confirmar>✓ Registrar volta</button>
        <button class="btn btn-claro" data-fechar>Cancelar</button>
      </div>`;
    det.querySelector('[data-fechar]')!.addEventListener('click', () => {
      det.hidden = true;
    });
    det.querySelector('[data-confirmar]')!.addEventListener('click', () => {
      void (async () => {
        const arq = det.querySelector<HTMLInputElement>('[data-foto]')!.files?.[0];
        const valor = Number(det.querySelector<HTMLInputElement>('[data-valor]')!.value);
        const empresa = det.querySelector<HTMLInputElement>('[data-empresa]')!.value.trim();
        if (!arq) return avisarBloqueante('Registre a foto da ferramenta concertada.');
        if (!(valor >= 0)) return avisarBloqueante('Informe o valor do concerto.');
        if (!empresa) return avisarBloqueante('Informe a empresa que consta no orçamento.');
        try {
          const foto = await api.enviarFoto('itens', arq, pat);
          const r = await api.post<{ divergente: boolean; empresaMotorista: string | null }>(
            `/api/ferramentas/${encodeURIComponent(pat)}/volta-concerto`,
            {
              fotoUrl: foto.caminho,
              valor,
              empresaOrcamento: empresa,
              estado: det.querySelector<HTMLSelectElement>('[data-estado]')!.value,
            },
          );
          if (r.divergente) {
            avisarBloqueante(
              `Atenção: o motorista deixou na "${r.empresaMotorista}", mas o orçamento veio de "${empresa}". Confira antes de pagar.`,
            );
          } else {
            avisar(`${pat} voltou ao CD.`);
          }
          void abrirVisao();
        } catch (e) {
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui registrar.');
        }
      })();
    });
  }

  // ---------------------------------------------------------------- histórico

  async function historico(pat: string): Promise<void> {
    corpo.innerHTML = `<p class="carregando">Abrindo o histórico de ${escapar(pat)}…</p>`;
    try {
      const h = await api.get<{
        unidade: Unidade & { criado_em: string };
        emprestimos: {
          saiu_em: string; reservado_ate: string; prazo_estendido_em: string | null;
          devolvido_em: string | null; estado_devolucao: string | null;
          responsavel_nome: string | null; obraNome: string | null; romaneio: string | null;
        }[];
        concertos: {
          enviado_em: string; deixado_em: string | null; voltou_em: string | null;
          defeito: string | null; valor: number | null; empresa_motorista: string | null;
          empresa_orcamento: string | null; estado_volta: string | null; romaneio: string | null;
        }[];
        totalGasto: number;
      }>(`/api/ferramentas/${encodeURIComponent(pat)}/historico`);
      const u = h.unidade;
      corpo.innerHTML = `
        <button class="voltar" id="fe-voltar">← Ferramentas</button>
        <section class="ped-painel">
          <div class="fe-card">
            <span class="ped-cfoto">${u.miniatura ? `<img src="${u.miniatura}" alt="" ${u.foto ? `data-grande="${escapar(api.foto(u.foto) ?? '')}"` : ''}>` : '<span class="sem">—</span>'}</span>
            <span class="ped-cnome">
              <b>${escapar(u.patrimonio)} — ${escapar(u.itemNome)}</b>
              <span class="sub-inline">${u.numero_serie ? `série ${escapar(u.numero_serie)} · ` : ''}${ESTADO_ROTULO[u.estado] ?? u.estado} · ${escapar(u.situacao)}</span>
              <span class="sub-inline"><b>Total gasto em concertos: R$ ${h.totalGasto.toFixed(2).replace('.', ',')}</b></span>
            </span>
          </div>

          <h3>Empréstimos <span class="sub-inline">${h.emprestimos.length}</span></h3>
          ${
            h.emprestimos.length === 0
              ? `<p class="dica">Nunca saiu do CD.</p>`
              : `<div class="rolagem"><table class="tabela"><thead><tr>
                   <th>Saiu</th><th>Com</th><th>Obra</th><th>Prazo</th><th>Voltou</th><th>Como voltou</th><th>Romaneio</th>
                 </tr></thead><tbody>${h.emprestimos
                   .map(
                     (e) => `<tr>
                     <td>${dataHora(e.saiu_em)}</td>
                     <td>${escapar(e.responsavel_nome ?? '—')}</td>
                     <td>${escapar(e.obraNome ?? '—')}</td>
                     <td>${dataBr(e.reservado_ate)}${e.prazo_estendido_em ? ' (estendido)' : ''}</td>
                     <td>${e.devolvido_em ? dataHora(e.devolvido_em) : 'fora'}</td>
                     <td>${e.estado_devolucao ? ESTADO_ROTULO[e.estado_devolucao] : '—'}</td>
                     <td class="cod">${escapar(e.romaneio ?? '—')}</td>
                   </tr>`,
                   )
                   .join('')}</tbody></table></div>`
          }

          <h3>Concertos <span class="sub-inline">${h.concertos.length}</span></h3>
          ${
            h.concertos.length === 0
              ? `<p class="dica">Nunca precisou de concerto.</p>`
              : `<div class="rolagem"><table class="tabela"><thead><tr>
                   <th>Enviado</th><th>Defeito</th><th>Empresa</th><th>Valor</th><th>Voltou</th><th>Estado</th>
                 </tr></thead><tbody>${h.concertos
                   .map(
                     (c) => `<tr>
                     <td>${dataHora(c.enviado_em)}</td>
                     <td>${escapar(c.defeito ?? '—')}</td>
                     <td>${escapar(c.empresa_orcamento ?? c.empresa_motorista ?? '—')}${
                       c.empresa_motorista &&
                       c.empresa_orcamento &&
                       c.empresa_motorista.toLowerCase() !== c.empresa_orcamento.toLowerCase()
                         ? ' ⚠️'
                         : ''
                     }</td>
                     <td class="num">${c.valor !== null ? 'R$ ' + Number(c.valor).toFixed(2).replace('.', ',') : '—'}</td>
                     <td>${c.voltou_em ? dataHora(c.voltou_em) : 'na loja'}</td>
                     <td>${c.estado_volta ? ESTADO_ROTULO[c.estado_volta] : '—'}</td>
                   </tr>`,
                   )
                   .join('')}</tbody></table></div>`
          }
        </section>`;
      corpo.querySelector('#fe-voltar')!.addEventListener('click', () => void abrirVisao());
    } catch (erro) {
      corpo.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir o histórico.',
      )}</p>`;
    }
  }

  // ------------------------------------------------------- folhas de concerto

  /**
   * As folhas impressas: UMA geral (todas as ferramentas do romaneio, com
   * campo em branco para o valor) e UMA POR FERRAMENTA, para prender nela.
   */
  async function imprimirFolhas(numeroRomaneio: string): Promise<void> {
    try {
      const r = await api.get<{
        numero: string;
        criado_em: string;
        motorista_nome: string | null;
        ferramentas: {
          patrimonio: string; numero_serie: string | null;
          itemNome: string; defeito: string | null;
        }[];
      }>(`/api/romaneios/${encodeURIComponent(numeroRomaneio)}`);

      const linha = (f: (typeof r.ferramentas)[0]) => `
        <tr>
          <td>${escapar(f.patrimonio)}</td>
          <td>${escapar(f.numero_serie ?? '—')}</td>
          <td>${escapar(f.itemNome)}</td>
          <td>${escapar(f.defeito ?? '—')}</td>
          <td class="valor"></td>
        </tr>`;

      const individual = (f: (typeof r.ferramentas)[0]) => `
        <div class="folha">
          <h1>Ademaldo Construções — Concerto de ferramenta</h1>
          <p class="rom">Romaneio ${escapar(r.numero)} · ${dataHora(r.criado_em)}</p>
          <table class="grande">
            <tr><th>Ferramenta</th><td>${escapar(f.itemNome)}</td></tr>
            <tr><th>Patrimônio</th><td class="destaque">${escapar(f.patrimonio)}</td></tr>
            <tr><th>Nº de série</th><td>${escapar(f.numero_serie ?? '—')}</td></tr>
            <tr><th>Defeito relatado</th><td>${escapar(f.defeito ?? '—')}</td></tr>
            <tr><th>Valor do concerto</th><td class="valor-grande">R$</td></tr>
            <tr><th>Empresa / carimbo</th><td class="valor-grande"></td></tr>
          </table>
          <p class="anexa">Prenda esta folha à ferramenta.</p>
        </div>`;

      const win = window.open('', '_blank');
      if (!win) {
        avisarBloqueante('O navegador bloqueou a janela de impressão — libere o pop-up.');
        return;
      }
      win.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
        <title>Concerto ${escapar(r.numero)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111; margin: 2rem; }
          h1 { font-size: 1.1rem; } .rom { color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
          th, td { border: 1px solid #444; padding: 0.5rem 0.6rem; text-align: left; font-size: 0.9rem; }
          thead th { background: #eee; }
          .valor { width: 8rem; }
          .folha { page-break-before: always; }
          .folha:first-child { page-break-before: auto; }
          .grande th { width: 11rem; background: #eee; }
          .grande td.destaque { font-size: 1.3rem; font-weight: bold; }
          .valor-grande { height: 3.2rem; font-size: 1.1rem; color: #555; vertical-align: bottom; }
          .anexa { margin-top: 1rem; color: #555; font-style: italic; }
        </style></head><body>
        <div class="folha">
          <h1>Ademaldo Construções — Romaneio de concerto ${escapar(r.numero)}</h1>
          <p class="rom">${dataHora(r.criado_em)}${r.motorista_nome ? ` · motorista: ${escapar(r.motorista_nome)}` : ''}</p>
          <table><thead><tr>
            <th>Patrimônio</th><th>Nº série</th><th>Ferramenta</th><th>Defeito</th><th>Valor do concerto</th>
          </tr></thead><tbody>${r.ferramentas.map(linha).join('')}</tbody></table>
        </div>
        ${r.ferramentas.map(individual).join('')}
        <script>window.onload = () => window.print();<\/script>
        </body></html>`);
      win.document.close();
    } catch (e) {
      avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui montar as folhas.');
    }
  }

  desenharVisoes();
  void abrirVisao();
}
